import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  InputAdornment,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Paper,
  Container,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Home,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchCustomers, searchCustomers } from '../../redux/slices/customerSlice';
import { CustomersTable } from '../../components/DataTable/examples';
import { CustomerData } from '../../components/DataTable/examples/CustomersTable';
import BackButton from '../../components/BackButton/BackButton';
import styles from './CustomersPage.module.css';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';

const CustomersPage: React.FC = () => {
  const [filter, setFilter] = useState<string>('Filtre por...');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setBreadcrumbs } = useBreadcrumb();

  const { items: customers, loading, error } = useAppSelector(state => state.customers);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
      { label: 'Painel de Clientes' },
    ]);

    return () => {
      setBreadcrumbs([{ label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> }]);
    };
  }, [setBreadcrumbs]);

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.trim() === '') {
      dispatch(fetchCustomers());
    } else {
      debounce(() => {
        dispatch(searchCustomers(value));
      }, 500)();
    }
  };

  const handleViewCustomer = (customerId: string) => {
    navigate(`/customers/${customerId}`);
  };

  // Transform customer data from API to table format
  const customerTableData: CustomerData[] = customers.map(customer => ({
    id: customer.id,
    company_name: customer.company_name,
    cnpj: customer.cnpj,
    sector: customer.sector,
    // In a real app, this would come from a related endpoint
    opportunityStatus: ['2 em aberto', 'Em análise', '1 fechada', 'Rejeitado'][
      Math.floor(Math.random() * 4)
    ],
  }));

  return (
    <>
      {/* Back button */}
      <BackButton sx={{ mb: 1 }} />

      {/* Header section */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: 'auto' },
            mb: { xs: 2, sm: 0 },
          }}
        >
          <Typography variant="h5">Painel de Clientes</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Aqui estão seus clientes ativos. Gerencie pedidos, oportunidades e acompanhe seus
            principais negócios.
          </Typography>
        </Box>

        <Button
          component={Link}
          to="/customers/new"
          variant="contained"
          startIcon={<PersonAddIcon sx={{ fontSize: 18 }} />}
          sx={{
            bgcolor: 'var(--acer-dark)',
            '&:hover': { bgcolor: '#333' },
            textTransform: 'none',
            borderRadius: '1px',
            color: 'white',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Adicionar Novo Cliente
        </Button>
      </Box>

      {/* Search and filter section */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <TextField
          placeholder="Buscar Cliente ou CNPJ"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          sx={{
            flexGrow: 1,
            minWidth: '250px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {loading ? (
                  <CircularProgress size={20} />
                ) : (
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                )}
              </InputAdornment>
            ),
          }}
        />

        {/* <FormControl
          sx={{
            minWidth: '200px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            },
          }}
          size="small"
        >
          <Select
            value={filter}
            displayEmpty
            onChange={e => setFilter(e.target.value)}
            IconComponent={() => <ExpandMoreIcon sx={{ color: 'text.secondary', mr: 1 }} />}
            renderValue={selected => (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {selected}
              </Typography>
            )}
          >
            <MenuItem value="Filtre por...">Filtre por...</MenuItem>
            <MenuItem value="Todos">Todos</MenuItem>
            <MenuItem value="Em aberto">Em aberto</MenuItem>
            <MenuItem value="Em análise">Em análise</MenuItem>
            <MenuItem value="Fechados">Fechados</MenuItem>
            <MenuItem value="Rejeitados">Rejeitados</MenuItem>
          </Select>
        </FormControl> */}
      </Box>

      {/* DataTable section */}
      <Paper elevation={1} sx={{ borderRadius: 1, overflow: 'hidden', p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <img
            src="/icons/painel-de-clientes.svg"
            alt="Quote icon"
            style={{
              width: 24,
              height: 24,
              marginRight: '8px',
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#2b3445', fontSize: '18px' }}>
            Meus clientes
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Carregando clientes...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
            <Typography>Erro ao carregar clientes: {error}</Typography>
          </Box>
        ) : (
          <CustomersTable data={customerTableData} onViewCustomer={handleViewCustomer} />
        )}
      </Paper>
    </>
  );
};

export default CustomersPage;
